import React, { useCallback, useId, useMemo, memo, ReactNode } from 'react';

import { EllipsisVerticalIcon, HatGlassesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StyledBadge } from '@/components/atoms/StyledBadge/StyledBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';
import { IconComponent } from '@/modules/humanChat/utils';
import { ViewType } from '@/shared/types/views';
import { isKeyEnter } from '@/util/util';

import { useNavNotification } from '../../hooks/useNavNotification';
import { MenuItem as MenuItemProps, NavNotificationKey } from '../../models';

type ItemProps = {
  children: ReactNode;
  customDisabled?: boolean;
  secondaryAction?: ReactNode;
  className?: string;
  onButtonClick?: () => void;
  onButtonKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

const Item = ({
  children,
  customDisabled,
  secondaryAction,
  className,
  onButtonClick,
  onButtonKeyDown,
  disabled,
  ref,
}: ItemProps & { ref?: React.Ref<HTMLLIElement> }) => {
  return (
    <li
      ref={ref}
      className={cn(
        'flex min-h-9 w-full min-w-32 items-center justify-between rounded border border-transparent text-sidebar-foreground',
        customDisabled
          ? 'cursor-not-allowed hover:bg-inherit'
          : 'hover:bg-selected-soft [&:hover_.nav-item-action]:opacity-100',
        className
      )}
    >
      <Button
        variant="ghost"
        onClick={onButtonClick}
        onKeyDown={onButtonKeyDown}
        disabled={disabled}
        className="flex h-full min-h-0 min-w-0 flex-1 cursor-pointer items-center gap-2 border-0 bg-transparent p-1.5 text-left font-normal text-muted-foreground hover:bg-transparent disabled:cursor-not-allowed [&_svg]:text-muted-foreground"
      >
        <div className="flex h-full min-h-0 min-w-0 flex-1 items-center gap-2 p-0">{children}</div>
      </Button>
      {secondaryAction && (
        // Pointer-only affordance: clicking the counter/gap navigates. Keyboard
        // nav is handled by the row's main button above.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
          className={cn('flex items-center px-0.5', !customDisabled && 'cursor-pointer')}
          onClick={!customDisabled ? onButtonClick : undefined}
        >
          {secondaryAction}
        </div>
      )}
    </li>
  );
};

// ============================================================================
// NavItem Component
// ============================================================================

// Memoized icon wrapper to prevent rerenders when badge updates
const IconWrapper = memo(({ icon }: { icon: React.ReactNode }) => {
  return <span className="flex shrink-0 items-center">{icon}</span>;
});

IconWrapper.displayName = 'IconWrapper';

const NotificationIcon = memo(
  ({
    icon,
    notificationKey,
  }: {
    icon: NonNullable<MenuItemProps['icon']>;
    notificationKey: NavNotificationKey;
  }) => {
    const hasNotification = useNavNotification(notificationKey);
    const iconElement = useMemo(() => <IconComponent icon={icon} />, [icon]);

    return (
      <StyledBadge invisible={!hasNotification}>
        <IconWrapper icon={iconElement} />
      </StyledBadge>
    );
  }
);

NotificationIcon.displayName = 'NotificationIcon';

// OptionsMenu component extracted outside to prevent recreation and state loss
const OptionsMenu = memo(({ options }: Pick<Required<MenuItemProps>, 'options'>) => {
  const { t } = useTranslation();

  const preventNavClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return (
    <DropdownMenu>
      <InfoTooltip content={t('common.options')} side="top">
        <DropdownMenuTrigger asChild>
          <Button
            onClick={preventNavClick}
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.view_menu')}
            className="nav-item-action mr-1.5 flex h-5 w-5 items-center justify-center rounded opacity-0 focus:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 data-[state=open]:opacity-100"
          >
            <EllipsisVerticalIcon size={16} />
          </Button>
        </DropdownMenuTrigger>
      </InfoTooltip>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {options.map(({ Icon, variant, label, fn }) => (
          <DropdownMenuItem key={label} onSelect={fn} variant={variant}>
            {Icon && (
              <Icon
                size={16}
                className={variant !== 'destructive' ? 'text-muted-foreground' : undefined}
              />
            )}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

OptionsMenu.displayName = 'OptionsMenu';

export const NavItem = memo(
  ({
    icon: Icon,
    text,
    onClick,
    sum,
    exactSum,
    notificationKey,
    selected = false,
    disabled = false,
    options,
    type,
  }: MenuItemProps & { selected: boolean; disabled: boolean; type?: ViewType }) => {
    const id = useId();
    const { t } = useTranslation();
    const { ref: nameRef, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [text]);

    const handleOnKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (onClick && isKeyEnter(event)) {
          onClick();
        }
      },
      [onClick]
    );

    // Memoize the icon section
    const iconSection = useMemo(() => {
      if (!Icon) {
        return null;
      }

      if (notificationKey) {
        return <NotificationIcon icon={Icon} notificationKey={notificationKey} />;
      }

      return (
        <StyledBadge invisible>
          <IconWrapper icon={<IconComponent icon={Icon} />} />
        </StyledBadge>
      );
    }, [Icon, notificationKey]);

    // Secondary action with hover menu for options
    const SecondaryAction = useMemo(() => {
      if (options?.length > 0) {
        return (
          <div
            role="presentation"
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {type === ViewType.PRIVATE && (
              <InfoTooltip content={t('conversation.views.private_view')} side="top">
                <span className="nav-item-action flex items-center opacity-0">
                  <HatGlassesIcon size={14} className="text-muted-foreground" />
                </span>
              </InfoTooltip>
            )}
            <OptionsMenu options={options} />
          </div>
        );
      }

      const sumSpan = (
        <span
          className={cn(
            'mr-1.5 w-5 cursor-pointer text-center text-xs leading-5 select-none',
            selected ? 'text-foreground' : 'text-muted-foreground'
          )}
        >
          {sum}
        </span>
      );

      // When the count is rounded (e.g. "51.5K"), show the exact number on hover.
      return exactSum ? (
        <InfoTooltip content={exactSum} side="top">
          {sumSpan}
        </InfoTooltip>
      ) : (
        sumSpan
      );
    }, [options, sum, exactSum, selected, type, t]);

    const listItem = (
      <Item
        customDisabled={disabled}
        className={cn(selected && 'border-border bg-background')}
        secondaryAction={SecondaryAction}
        onButtonClick={onClick}
        onButtonKeyDown={handleOnKeyDown}
        disabled={disabled}
      >
        {iconSection}
        <InfoTooltip content={!disabled && isTruncated ? text : undefined} side="top">
          <span
            id={id}
            ref={nameRef}
            className="m-0 min-w-0 flex-1 overflow-hidden text-sm leading-5 text-ellipsis whitespace-nowrap text-foreground"
          >
            {text}
          </span>
        </InfoTooltip>
      </Item>
    );

    return disabled ? (
      <InfoTooltip content={t('conversations.no_permission')} delayDuration={500}>
        <div>{listItem}</div>
      </InfoTooltip>
    ) : (
      listItem
    );
  }
);

NavItem.displayName = 'NavItem';
